import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apprenticeApi } from '../api/apprentices';
import { trackingApi } from '../api/tracking';
import { Apprentice } from '../types';

interface OTJFormData {
  tenant_id: string;
  apprentice_id: string;
  activity_date: string;
  hours: number;
  activity_type: string;
  description: string;
  evidence_url: string;
}

const activityTypes = [
  { value: 'training', label: 'Training Session' },
  { value: 'mentoring', label: 'Mentoring' },
  { value: 'self-study', label: 'Self-Study' },
  { value: 'workshop', label: 'Workshop' },
  { value: 'shadowing', label: 'Job Shadowing' },
  { value: 'research', label: 'Research & Development' },
  { value: 'other', label: 'Other' },
];

export const LogOTJ: React.FC = () => {
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  
  const [apprentices, setApprentices] = useState<Apprentice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<OTJFormData>({
    tenant_id: user?.tenant_id || '',
    apprentice_id: '',
    activity_date: new Date().toISOString().split('T')[0],
    hours: 1,
    activity_type: '',
    description: '',
    evidence_url: '',
  });

  const fetchApprentices = useCallback(async () => {
    if (!user?.tenant_id) return;
    
    try {
      const response = await apprenticeApi.getAll(user.tenant_id);
      setApprentices(response.data);

      // For learners, auto-select their own apprentice record
      if (userRole === 'learner' && user?.user_id && user?.email) {
        const myApprentice = response.data.find(
          (a: Apprentice) => a.canvas_user_id === user.user_id || a.email === user.email
        );
        
        if (myApprentice) {
          setFormData(prev => ({
            ...prev,
            apprentice_id: myApprentice.id
          }));
        }
      }
    } catch (err) {
      console.error('Failed to load apprentices:', err);
    }
  }, [user?.tenant_id, user?.user_id, user?.email, userRole]);

  useEffect(() => {
    fetchApprentices();
  }, [fetchApprentices]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'hours' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (formData.hours < 0.25) {
      setError('Hours must be at least 0.25 (15 minutes)');
      return;
    }

    if (!formData.apprentice_id) {
      setError('Please select an apprentice');
      return;
    }

    if (!formData.activity_type) {
      setError('Please select an activity type');
      return;
    }

    setLoading(true);

    try {
      // Remove empty optional fields
      const submitData: any = { ...formData };
      if (!submitData.evidence_url) {
        delete submitData.evidence_url;
      }

      await trackingApi.createOTJ(submitData);
      
      // Navigate based on role
      if (userRole === 'learner') {
        navigate('/');  // Learners go to dashboard
      } else {
        navigate('/tracking');  // Instructors/admins go to tracking page
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to log OTJ hours');
    } finally {
      setLoading(false);
    }
  };

  const selectedApprentice = apprentices.find(a => a.id === formData.apprentice_id);

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Log OTJ Hours</h2>
        <p className="text-gray-600">Record off-the-job training activity</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
        {/* Apprentice Selection - Only show for instructors/admins */}
        {userRole !== 'learner' && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Apprentice</h3>
            <div>
              <label htmlFor="apprentice_id" className="block text-sm font-medium text-gray-700 mb-2">
                Select Apprentice <span className="text-red-500">*</span>
              </label>
              <select
                id="apprentice_id"
                name="apprentice_id"
                value={formData.apprentice_id}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select an apprentice...</option>
                {apprentices.map((apprentice) => (
                  <option key={apprentice.id} value={apprentice.id}>
                    {apprentice.first_name} {apprentice.last_name} - {apprentice.email}
                  </option>
                ))}
              </select>
              {selectedApprentice && (
                <p className="mt-2 text-sm text-gray-600">
                  Current OTJ Progress: {selectedApprentice.otj_percentage}%
                  {selectedApprentice.employer_name && ` | Employer: ${selectedApprentice.employer_name}`}
                </p>
              )}
            </div>
          </div>
        )}

        {/* For learners, show their name instead */}
        {userRole === 'learner' && selectedApprentice && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <span className="font-semibold">Logging hours for:</span> {selectedApprentice.first_name} {selectedApprentice.last_name}
            </p>
            <p className="text-sm text-blue-800 mt-1">
              Current OTJ Progress: {selectedApprentice.otj_percentage}%
            </p>
          </div>
        )}

        {/* Activity Details */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="activity_date" className="block text-sm font-medium text-gray-700 mb-2">
                Activity Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="activity_date"
                name="activity_date"
                value={formData.activity_date}
                onChange={handleChange}
                max={new Date().toISOString().split('T')[0]}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="hours" className="block text-sm font-medium text-gray-700 mb-2">
                Hours <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="hours"
                name="hours"
                value={formData.hours}
                onChange={handleChange}
                min="0.25"
                step="0.25"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">Minimum 0.25 hours (15 minutes)</p>
            </div>

            <div className="md:col-span-2">
              <label htmlFor="activity_type" className="block text-sm font-medium text-gray-700 mb-2">
                Activity Type <span className="text-red-500">*</span>
              </label>
              <select
                id="activity_type"
                name="activity_type"
                value={formData.activity_type}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select activity type...</option>
                {activityTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                required
                placeholder="Describe the learning activity and what was covered..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="evidence_url" className="block text-sm font-medium text-gray-700 mb-2">
                Evidence URL (Optional)
              </label>
              <input
                type="url"
                id="evidence_url"
                name="evidence_url"
                value={formData.evidence_url}
                onChange={handleChange}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Link to supporting evidence (e.g., certificate, assignment, project)
              </p>
            </div>
          </div>
        </div>

        {/* Information Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">Off-the-Job Training Requirements</h4>
          <p className="text-sm text-blue-800">
            Apprentices must spend at least 20% of their time on off-the-job training. This includes:
            learning new skills, knowledge acquisition, training sessions, mentoring, and shadowing.
            Time spent on productive work does not count towards OTJ hours.
          </p>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate(userRole === 'learner' ? '/' : '/tracking')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
          >
            {loading ? 'Logging...' : 'Log OTJ Hours'}
          </button>
        </div>
      </form>
    </div>
  );
};
